"use client";

import {
  useEffect,
  useRef,
  useState,
  type CSSProperties,
} from "react";
import { createPortal } from "react-dom";
import { Maximize2, Settings } from "lucide-react";
import type { ChatShareProvider } from "@/src/lib/chat-share/types";
import type { PdfOptions } from "@/src/lib/pdf/print-document";
import type { PdfFontKey } from "@/src/lib/pdf/fonts";
import type { EditorPreferencesSource } from "@/src/lib/storage/user-preferences";
import {
  EditorToolbar,
  useToolbarCompact,
} from "../editor/editor-toolbar";
import { navigateToFullscreenEditor } from "../editor/editor-handoff";
import { OpenMarkdownFileButton } from "../editor/open-markdown-file-button";
import { ClearDocumentButton } from "../editor/clear-document-button";
import type { ReadMarkdownFileSuccess } from "../editor/read-markdown-file";
import type { EditorCommand } from "../editor/types";
import { FilenameDownloadButton } from "../preview/filename-download-button";
import { PreviewOptionsMenu } from "../preview/preview-options-menu";
import type { DownloadState } from "../preview/types";
import { classNames } from "../ui/class-names";

const SETTINGS_MENU_GAP_PX = 8; // matches former mt-2
const SETTINGS_MENU_GUTTER_PX = 12;
const SETTINGS_MENU_MAX_WIDTH_PX = 26 * 16; // 26rem
const SETTINGS_MENU_MIN_HEIGHT_PX = 160;

export function WorkspaceToolbar({
  canRedo,
  canUndo,
  onCommand,
  onRedo,
  onUndo,
  pdfOptions,
  onPdfOptionsChange,
  editorFontFamily,
  editorFontSize,
  onEditorFontFamilyChange,
  onEditorFontSizeChange,
  onClosePopovers,
  filename,
  onFilenameChange,
  canDownload,
  downloadState,
  onDownload,
  showPdfSettings = true,
  showOpenFile = false,
  onOpenMarkdownFile,
  showClear = false,
  canClear = false,
  onClearDocument,
  showOpenFullscreen = false,
  editorSource = "editor",
  fallbackMarkdown = "",
  fallbackFilename = "",
  chatShareProvider,
  chatShareImportUrl,
  repoMarkdownImport = false,
  repoMarkdownImportUrl,
  lineNumberDigits = 2,
}: Readonly<{
  canRedo: boolean;
  canUndo: boolean;
  onCommand: (command: EditorCommand) => void;
  onRedo: () => void;
  onUndo: () => void;
  pdfOptions: PdfOptions;
  onPdfOptionsChange: (options: PdfOptions) => void;
  editorFontFamily: PdfFontKey;
  editorFontSize: number;
  onEditorFontFamilyChange: (fontFamily: PdfFontKey) => void;
  onEditorFontSizeChange: (fontSize: number) => void;
  onClosePopovers?: (close: () => void) => void;
  filename: string;
  onFilenameChange: (filename: string) => void;
  canDownload: boolean;
  downloadState: DownloadState;
  onDownload: () => void;
  showPdfSettings?: boolean;
  showOpenFile?: boolean;
  onOpenMarkdownFile?: (result: ReadMarkdownFileSuccess) => void;
  showClear?: boolean;
  canClear?: boolean;
  onClearDocument?: () => void;
  /** Embedded workspaces link to the full-screen editor with settings handoff. */
  showOpenFullscreen?: boolean;
  editorSource?: EditorPreferencesSource;
  fallbackMarkdown?: string;
  fallbackFilename?: string;
  chatShareProvider?: ChatShareProvider;
  chatShareImportUrl?: string;
  repoMarkdownImport?: boolean;
  repoMarkdownImportUrl?: string;
  /** Keep open/clear column width in sync with the editor line-number gutter. */
  lineNumberDigits?: number;
}>) {
  const menuRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [menuStyle, setMenuStyle] = useState<CSSProperties | null>(null);
  const [openFileError, setOpenFileError] = useState<string | null>(null);
  const { containerRef, density } = useToolbarCompact();
  const isCompactExport = density === "narrow";
  // Phones (~414px) cannot fit filename + formatting controls on one row.
  // Until density is measured, keep the filename in the download control and
  // let @container queries collapse it visually (avoids desktop/mobile jumps).
  const hideFilename = isCompactExport;

  function measureMenuStyle(): CSSProperties | null {
    const anchor = buttonRef.current;
    if (!anchor || typeof window === "undefined") {
      return null;
    }

    const rect = anchor.getBoundingClientRect();
    const visualViewport = window.visualViewport;
    const viewportLeft = visualViewport?.offsetLeft ?? 0;
    const viewportTop = visualViewport?.offsetTop ?? 0;
    const viewportRight =
      viewportLeft + (visualViewport?.width ?? window.innerWidth);
    const viewportBottom =
      viewportTop + (visualViewport?.height ?? window.innerHeight);

    // Prefer the nearest overflow clip (embedded shell is overflow-hidden)
    // over the visual viewport so the panel stays fully on-screen.
    let clipLeft = viewportLeft;
    let clipRight = viewportRight;
    let clipBottom = viewportBottom;
    let node: HTMLElement | null = anchor.parentElement;
    while (node) {
      const style = window.getComputedStyle(node);
      const clipsX =
        style.overflowX === "hidden" ||
        style.overflowX === "clip" ||
        style.overflow === "hidden" ||
        style.overflow === "clip";
      const clipsY =
        style.overflowY === "hidden" ||
        style.overflowY === "clip" ||
        style.overflow === "hidden" ||
        style.overflow === "clip";

      if (clipsX || clipsY) {
        const clipRect = node.getBoundingClientRect();
        if (clipsX) {
          clipLeft = Math.max(clipLeft, clipRect.left);
          clipRight = Math.min(clipRight, clipRect.right);
        }
        if (clipsY) {
          clipBottom = Math.min(clipBottom, clipRect.bottom);
        }
        break;
      }
      node = node.parentElement;
    }

    const availableWidth = Math.max(
      0,
      clipRight - clipLeft - SETTINGS_MENU_GUTTER_PX * 2,
    );
    const width = Math.min(SETTINGS_MENU_MAX_WIDTH_PX, availableWidth);
    // Prefer right-aligning to the trigger; clamp so the panel stays in clip.
    const left = Math.min(
      Math.max(rect.right - width, clipLeft + SETTINGS_MENU_GUTTER_PX),
      clipRight - width - SETTINGS_MENU_GUTTER_PX,
    );
    const top = rect.bottom + SETTINGS_MENU_GAP_PX;
    const maxHeight = Math.max(
      SETTINGS_MENU_MIN_HEIGHT_PX,
      Math.floor(clipBottom - top - SETTINGS_MENU_GUTTER_PX),
    );

    return {
      position: "fixed",
      top,
      left,
      width,
      maxHeight,
    };
  }

  useEffect(() => {
    onClosePopovers?.(() => {
      setIsMenuOpen(false);
      setMenuStyle(null);
    });
  }, [onClosePopovers]);

  useEffect(() => {
    if (!isMenuOpen) {
      return;
    }

    function updateMenuStyle(event?: Event) {
      const target = event?.target;
      if (
        target instanceof Node &&
        menuRef.current &&
        (target === menuRef.current || menuRef.current.contains(target))
      ) {
        return;
      }

      setMenuStyle(measureMenuStyle());
    }

    function handleClickOutside(event: MouseEvent) {
      const target = event.target as Node;

      if (
        menuRef.current?.contains(target) ||
        buttonRef.current?.contains(target) ||
        // Theme/font selects portal their lists to document.body.
        (target instanceof Element &&
          target.closest("[data-document-settings-dropdown]"))
      ) {
        return;
      }

      setIsMenuOpen(false);
      setMenuStyle(null);
    }

    function handleKeyDown(event: globalThis.KeyboardEvent) {
      if (event.key === "Escape") {
        setIsMenuOpen(false);
        setMenuStyle(null);
      }
    }

    updateMenuStyle();
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleKeyDown);
    window.addEventListener("scroll", updateMenuStyle, true);
    window.addEventListener("resize", updateMenuStyle);
    window.visualViewport?.addEventListener("resize", updateMenuStyle);
    window.visualViewport?.addEventListener("scroll", updateMenuStyle);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("scroll", updateMenuStyle, true);
      window.removeEventListener("resize", updateMenuStyle);
      window.visualViewport?.removeEventListener("resize", updateMenuStyle);
      window.visualViewport?.removeEventListener("scroll", updateMenuStyle);
    };
  }, [isMenuOpen]);

  const iconButtonClassName = classNames(
    "inline-flex shrink-0 items-center justify-center rounded-md transition focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand",
    isCompactExport ? "size-8" : "size-10 @max-[900px]/toolbar:size-8",
  );
  const iconClassName = isCompactExport
    ? "size-4"
    : "size-5 @max-[900px]/toolbar:size-4";

  return (
    <div
      ref={containerRef}
      data-workspace-toolbar=""
      // Above editor z-10 chrome so the options popover receives taps on mobile.
      className={classNames(
        "@container/toolbar relative z-20 flex h-14 shrink-0 items-center border-b border-zinc-200 bg-[var(--surface-toolbar)] shadow-[0_1px_3px_rgba(0,0,0,0.07)]",
      )}
    >
      {density !== null ? (
        <>
          {showOpenFile && onOpenMarkdownFile ? (
            <div
              className="editor-gutter-width flex h-full w-[var(--gutter-width)] shrink-0 items-center justify-center border-r border-[var(--border-subtle)]"
              style={
                {
                  "--line-number-digits": lineNumberDigits,
                } as CSSProperties
              }
            >
              <OpenMarkdownFileButton
                compact
                onLoaded={(result) => {
                  setOpenFileError(null);
                  onOpenMarkdownFile(result);
                }}
                onError={setOpenFileError}
              />
              {showClear && onClearDocument ? (
                <ClearDocumentButton
                  compact
                  disabled={!canClear}
                  onClear={onClearDocument}
                />
              ) : null}
            </div>
          ) : showClear && onClearDocument ? (
            <div
              className="editor-gutter-width flex h-full w-[var(--gutter-width)] shrink-0 items-center justify-center border-r border-[var(--border-subtle)]"
              style={
                {
                  "--line-number-digits": lineNumberDigits,
                } as CSSProperties
              }
            >
              <ClearDocumentButton
                compact
                disabled={!canClear}
                onClear={onClearDocument}
              />
            </div>
          ) : null}
          <EditorToolbar
            canRedo={canRedo}
            canUndo={canUndo}
            onCommand={onCommand}
            onRedo={onRedo}
            onUndo={onUndo}
            density={density}
          />
        </>
      ) : (
        <div className="min-w-0 flex-1" aria-hidden="true" />
      )}

      <div
        className={classNames(
          "relative z-10 flex shrink-0 items-center bg-[var(--surface-toolbar)] py-2",
          isCompactExport
            ? "gap-0.5 pl-0.5 pr-1.5"
            : "gap-1 pl-1 pr-2 sm:gap-2 sm:px-3 @max-[900px]/toolbar:gap-0.5 @max-[900px]/toolbar:pl-0.5 @max-[900px]/toolbar:pr-1.5",
        )}
        aria-label="PDF export controls"
      >
        <FilenameDownloadButton
          filename={filename}
          onFilenameChange={onFilenameChange}
          canDownload={canDownload}
          downloadState={downloadState}
          onDownload={onDownload}
          compact={isCompactExport}
          hideFilename={showPdfSettings ? hideFilename : false}
        />
        {showPdfSettings ? (
          <div className="relative">
            <button
              ref={buttonRef}
              type="button"
              aria-haspopup="dialog"
              aria-expanded={isMenuOpen}
              aria-label="Document settings"
              title="Customize the theme, page size, fonts, and margins in Settings."
              onClick={() => {
                if (isMenuOpen) {
                  setIsMenuOpen(false);
                  setMenuStyle(null);
                  return;
                }
                setMenuStyle(measureMenuStyle());
                setIsMenuOpen(true);
              }}
              className={classNames(
                iconButtonClassName,
                isMenuOpen
                  ? "bg-zinc-100 text-zinc-950"
                  : "text-zinc-600 hover:bg-zinc-100 hover:text-zinc-950",
              )}
            >
              <Settings aria-hidden="true" className={iconClassName} />
            </button>

            {isMenuOpen && menuStyle
              ? createPortal(
                  <div
                    ref={menuRef}
                    // Width/left/maxHeight are measured against the viewport and
                    // nearest overflow clip so the panel stays fully visible on
                    // narrow mobile shells (avoids right-0 + 100vw overflow).
                    className="z-50 flex flex-col overflow-hidden rounded-xl border border-zinc-200 bg-white shadow-lg"
                    style={menuStyle}
                  >
                    {hideFilename ? (
                      <div className="shrink-0 border-b border-zinc-200 px-4 py-3">
                        <label className="flex flex-col gap-1.5">
                          <span className="text-xs font-medium text-zinc-600">
                            File name
                          </span>
                          <input
                            aria-label="Filename"
                            autoComplete="off"
                            maxLength={140}
                            placeholder="File name..."
                            value={filename}
                            onChange={(event) =>
                              onFilenameChange(event.target.value)
                            }
                            className="h-10 rounded-md border border-zinc-200 bg-zinc-50 px-3 text-sm text-zinc-950 outline-none transition placeholder:text-zinc-400 hover:bg-zinc-100 focus:border-brand focus:bg-white focus:ring-2 focus:ring-brand/20"
                          />
                        </label>
                      </div>
                    ) : null}
                    <PreviewOptionsMenu
                      pdfOptions={pdfOptions}
                      onPdfOptionsChange={onPdfOptionsChange}
                      editorFontFamily={editorFontFamily}
                      editorFontSize={editorFontSize}
                      onEditorFontFamilyChange={onEditorFontFamilyChange}
                      onEditorFontSizeChange={onEditorFontSizeChange}
                      onClose={() => {
                        setIsMenuOpen(false);
                        setMenuStyle(null);
                      }}
                    />
                  </div>,
                  document.body,
                )
              : null}
          </div>
        ) : null}
        {showOpenFullscreen ? (
          <button
            type="button"
            aria-label="Open full-screen editor"
            title="Open full-screen editor"
            onClick={() => {
              navigateToFullscreenEditor({
                fallbackMarkdown,
                fallbackFilename,
                source: editorSource,
                settings: {
                  pdfOptions,
                  editorFontFamily,
                  editorFontSize,
                },
                chatShareProvider,
                chatShareImportUrl,
                repoMarkdownImport,
                repoMarkdownImportUrl,
              });
            }}
            className={classNames(
              iconButtonClassName,
              "text-zinc-600 hover:bg-zinc-100 hover:text-zinc-950",
            )}
          >
            <Maximize2 aria-hidden="true" className={iconClassName} />
          </button>
        ) : null}
      </div>

      {downloadState.status === "error" ? (
        <p
          role="alert"
          className="absolute right-3 top-[calc(100%-0.25rem)] z-40 max-w-xs rounded-md border border-red-200 bg-red-50 px-2 py-1 text-xs font-medium text-red-700 shadow-sm"
        >
          {downloadState.errorMessage}
        </p>
      ) : null}
      {openFileError ? (
        <p
          role="alert"
          className="absolute left-3 top-[calc(100%-0.25rem)] z-40 max-w-xs rounded-md border border-red-200 bg-red-50 px-2 py-1 text-xs font-medium text-red-700 shadow-sm"
        >
          {openFileError}
        </p>
      ) : null}
    </div>
  );
}
